import queryString from 'query-string'

// The reserved overlay-param convention (EX-39749 / EX-39962), kept dependency-
// free (no react-router) so the ext-ui filter engine can import it without
// pulling routing into the design-system layer. `modalParams.ts` re-exports
// these alongside the routing hooks.

export const MODAL_LAYERS = ['modal', 'flyout'] as const
export type ModalLayer = (typeof MODAL_LAYERS)[number]

// Matches a reserved overlay param: the bare layer key, or `<layer><CamelKey>`.
export const RESERVED_MODAL_PARAM = /^(modal|flyout)([A-Z]\w*)?$/

export const isReservedModalParam = (key: string): boolean =>
  RESERVED_MODAL_PARAM.test(key)

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

// `('modal', 'id')` -> `'modalId'`; `('flyout', 'firstName')` -> `'flyoutFirstName'`.
// The one place the `<layer><CamelKey>` data-param convention is defined; both
// the routing hooks (`modalParams.ts`) and the legacy-deeplink redirect registry
// (`overlayRoutes.ts`) build param keys through this so they can never drift.
export const toLayerParamKey = (layer: ModalLayer, subKey: string): string =>
  `${layer}${capitalize(subKey)}`

/**
 * Re-apply the reserved overlay params from `currentSearch` onto `nextSearch`.
 *
 * Filter writers rebuild the search string from filter state only, dropping any
 * non-filter param. Call this immediately before every `history.push/replace`
 * in a filter writer so an open overlay survives a filter change. Reserved
 * params win over `nextSearch` — the overlay is independent of the filters.
 *
 * When no overlay is open, `nextSearch` is returned untouched (no parse/restring)
 * so a filter writer keeps its exact serialization in the common case.
 */
export const preserveReservedParams = (
  currentSearch: string,
  nextSearch: string,
): string => {
  const current = queryString.parse(currentSearch)
  const reservedKeys = Object.keys(current).filter(isReservedModalParam)
  if (reservedKeys.length === 0) return nextSearch
  const next = queryString.parse(nextSearch)
  const reserved: ReturnType<typeof queryString.parse> = {}
  reservedKeys.forEach((key) => {
    reserved[key] = current[key]
  })
  return queryString.stringify({ ...next, ...reserved })
}
